import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {IRegularExerciseConfig, IRegularExerciseForm} from "../../../shared/interfaces/forms/exercises/IRegularExerciseForm";
import {EExerciseType} from "../../../shared/enums/EExerciseType";
import {EExerciseStartType} from "../../../shared/enums/EExerciseStartType";
import {IExerciseConfig} from "../../../features/exercises/exercise-form/exercise-form.component";
import { IRegularExerciseConfigForm } from "../factory/RegularExerciseFormFactory";
import {EExerciseJumpType} from "../../../shared/enums/EExerciseJumpType";

export interface ExistingExercisePayloadState {
  boardType?: number;
  imageUrl?: string;
  isFavorite?: boolean;
  thumbnailUrl?: string;
}

export class RegularExerciseHelper {
  static currentForm(
    formGroup: FormGroup<IRegularExerciseForm>,
    type: FormControl,
  ) {
    switch (type.value) {
      case 0:
        return formGroup.controls.config.controls.countForm;
      case 1:
        return formGroup.controls.config.controls.reactForm;
      case 2:
        return formGroup.controls.config.controls.sequenceForm;
      case 3:
        return formGroup.controls.config.controls.agilityForm;
      case 4:
        return formGroup.controls.config.controls.verticalForm;
      case 5:
        return formGroup.controls.config.controls.companionForm;
      default:
        type.setValue(0);
        return formGroup.controls.config.controls.countForm;
    }
  }

  static isFormValid(
    formGroup: FormGroup<IRegularExerciseForm>,
    type: FormControl,
  ): boolean {
    let isValid = RegularExerciseHelper.currentForm(
      formGroup,
      formGroup.controls.type,
    ).valid;
    if (type.value !== EExerciseType.Vertical)
      isValid =
        isValid && formGroup.controls.config.controls.countdownForm.valid;
    isValid =
      isValid &&
      formGroup.controls.name.valid &&
      formGroup.controls.description.valid &&
      formGroup.controls.config.controls?.difficulty.valid &&
      formGroup.controls.category.valid;
    return isValid;
  }

  static createPayload(
    formGroup: FormGroup<IRegularExerciseForm>,
    existingExercise?: ExistingExercisePayloadState,
  ) {
    const selectedImageUrl = formGroup.controls.imgThumb?.value;
    const existingDisplayImage =
      existingExercise?.thumbnailUrl ??
      existingExercise?.imageUrl ??
      "";
    const imageWasReplaced =
      !existingExercise || selectedImageUrl !== existingDisplayImage;

    return {
      videoUrl: formGroup.controls.video?.controls.fileUrl.value,
      imageUrl: imageWasReplaced
        ? selectedImageUrl
        : existingExercise?.imageUrl ?? selectedImageUrl,
      thumbnailUrl: imageWasReplaced
        ? selectedImageUrl
        : existingExercise?.thumbnailUrl ?? selectedImageUrl,
      name: formGroup.controls.name.value,
      description: RegularExerciseHelper.descriptionPayload(formGroup),
      categoryId: formGroup.controls.category.value.id,
      type: formGroup.controls.type.value,
      boardType: existingExercise?.boardType ?? 0,
      isFavorite: existingExercise?.isFavorite ?? false,
      bodyPart: formGroup.controls.bodyPart.value,
      config: RegularExerciseHelper.configPayload(formGroup),
    };
  }

  static configPayload(formGroup: FormGroup<IRegularExerciseForm>): IExerciseConfig {
    return this.getConfigRaw(formGroup.controls.type.value, formGroup.controls.config as FormGroup<IRegularExerciseConfigForm>);
  }

  public static getConfigRaw(exerciseType: EExerciseType, config: FormGroup<IRegularExerciseConfigForm>): IExerciseConfig {
    const countdownForm = config.controls.countdownForm;
    const countForm = config.controls.countForm;
    const reactForm = config.controls.reactForm;
    const sequenceForm = config.controls.sequenceForm;
    const agilityForm = config.controls.agilityForm;
    const verticalForm = config.controls.verticalForm;
    const companionForm = config.controls.companionForm;
    const activeSensors = RegularExerciseHelper.getreactSensorsArray(
      reactForm.controls.activeSensors,
    );

    const countdowns = {
      countdownStartType: countdownForm.controls.startType.value,
      countdownEndType: countdownForm.controls.endType.value,
      countdownEnd: countdownForm.controls.endValue.value,
      countdownStart: countdownForm.controls.startValue.value,
    };

    if (
        config.controls.countdownForm.controls.startType
            .value === EExerciseStartType.RANDOM
    )
      delete countdowns.countdownStart;

    let configPayload: any = {
      difficulty: config.controls?.difficulty.value,
    };

    switch (exerciseType) {
      case 0:
        configPayload = {
          ...configPayload,
          ...countdowns,
          sumType: countForm.controls.countType.value,
        };
        break;

      case 1:
        configPayload = {
          ...configPayload,
          ...countdowns,
          timerDelay: reactForm.controls.delay.value,
          timerPrompt: reactForm.controls.timerPrompt.value === 0 ? null : reactForm.controls.timerPrompt.value,
          reactTo: RegularExerciseHelper.getReactArray(
              reactForm.controls.reactTo,
          ),
          dontReactTo: RegularExerciseHelper.getReactArray(
              reactForm.controls.dontReactTo,
          ),
          activeSensors: activeSensors.length > 0
            ? activeSensors
            : [1, 2, 3, 4, 5],
          neurocognitiveType: reactForm.controls.neurocognitiveType.value,
          isolatedReact: reactForm.controls.isolatedReact.value,
          liftTime: reactForm.controls.liftTime.value,
          stroopEffectType: reactForm.controls.stroopEffectType.value,
          footFireActivity: reactForm.controls.footFireActivity.value
        };
        break;

      case 2: //sequence
        Object.assign(configPayload, {
          ...countdowns,
          timerDelay: sequenceForm.controls["delay"].value,
          timerPrompt: sequenceForm.controls["timerPrompt"].value === 0 ? null : sequenceForm.controls["timerPrompt"].value,
          sensorsSequence: sequenceForm.controls.sequenceSensors.value,
        });
        break;

      case 3: //agility
        Object.assign(configPayload, {
          ...countdowns,
          timerDelay: agilityForm.controls.delay.value,
          arrowsType: agilityForm.controls.agilityCustomType.value,
          arrows: agilityForm.controls.agilitySensors.value,
          timerPrompt: agilityForm.controls.timerPrompt.value === 0 ? null : agilityForm.controls.timerPrompt.value,
        });
        break;

      case 4: //vertical
        Object.assign(configPayload, {
          numberOfLegs: verticalForm.controls.numberOfLegs.value,
          jumpType: verticalForm.controls.verticalJumpType.value,
          jumpCount: verticalForm.controls.numberOfJumps.value,
          verticalReact: verticalForm.controls.isVerticalReact.value,
          reactTo: RegularExerciseHelper.getReactArray(
              verticalForm.controls.reactTo,
          ),
          dontReactTo: RegularExerciseHelper.getReactArray(
              verticalForm.controls.dontReactTo,
          ),
        });
        break;

      case 5: // companion
        Object.assign(configPayload, {
          ...countdowns,
          modeType: companionForm.controls.companionMode.value,
          companionsCount: companionForm.controls.numberOfCompanions.value,
          sensorsCount: companionForm.controls.numberOfSensors.value,
          agilityType: companionForm.controls.agilityType?.value,
          arrowsType: companionForm.controls.arrowsType.value,
          arrows: companionForm.controls.arrows.value,
          numberType: companionForm.controls.numberType?.value,
          countIncrementBy: companionForm.controls.countIncrementBy?.value,
          countDirectionType: companionForm.controls.countDirection?.value === 'Ascending' ? 0 : 1,
          complexityLevel: ((): number => {
            const c = companionForm.controls.complexityLevel?.value;
            if (c === 'Intermediate') return 1; if (c === 'Advanced') return 2; return 0;
          })(),
          reactTo: companionForm.controls.reactTo?.value
            .map((v, i) => (v ? i : null))
            .filter((v) => v !== null),
          dontReactTo: companionForm.controls.dontReactTo?.value
            .map((v, i) => (v ? i : null))
            .filter((v) => v !== null),
          timerDelay: companionForm.controls.delay?.value,
          timerPrompt: companionForm.controls.timerPrompt?.value === 0 ? null : companionForm.controls.timerPrompt?.value,
          reactCommunicationType: companionForm.controls.communicationType?.value,
          neurocognitiveType: companionForm.controls.neurocognitiveType?.value,
          stroopEffectType: companionForm.controls.stroopEffectType?.value,
          isolatedReact: companionForm.controls.isolatedReact?.value,
          activeSensors:
            companionForm.controls.companionMode.value === 0 &&
            companionForm.controls.simultaneousType.value === 0
              ? []
              : RegularExerciseHelper.getreactSensorsArray(
                  companionForm.controls.activeSensors,
                ),
          simultaneousType: companionForm.controls.simultaneousType?.value,
          sensorsSequence: companionForm.controls.sequenceSensors.value,
          startValue: companionForm.controls.startValue?.value,
          endValue: companionForm.controls.endValue?.value,
        });
        break;

      default:
        break;
    }

    return configPayload;
  }

  static getReactArray(formArray: FormArray<FormControl<boolean>>): number[] {
    return formArray.value
      .map((value, index) => (value ? index : null))
      .filter((value) => value !== null);
  }

  static descriptionPayload(
    formGroup: FormGroup<IRegularExerciseForm>,
  ): string | null {
    const description = formGroup.controls.description.value ?? "";
    const originalHtml = formGroup.controls.originalDescriptionHtml.value ?? "";
    if (description.trim().length === 0) return null;
    return description === RegularExerciseHelper.descriptionToPlainText(originalHtml)
      ? originalHtml
      : RegularExerciseHelper.descriptionToHtml(description);
  }

  static descriptionToPlainText(html: string): string {
    if (!html) return "";
    const normalized = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li)>/gi, "\n");
    const element = document.createElement("div");
    element.innerHTML = normalized;
    return (element.textContent ?? "")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/\s+$/, "");
  }

  static descriptionToHtml(description: string): string {
    if (!description) return "";
    return description
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
      .replace(/\r?\n/g, "<br>");
  }

  static getreactSensorsArray(
    formArray: FormArray<FormControl<boolean>>,
  ): number[] {
    return formArray.value
      .map((value, index) => (value ? index + 1 : null))
      .filter((value) => value !== null);
  }

  static patchFormConfig(
    formGroup: FormGroup<IRegularExerciseForm>,
    exercise: any): void {
    const selectedConfig = exercise.configs.find(
        (config) => config.id === exercise.defaultConfigId,
    );
    if (!selectedConfig) return;

    const timerPrompt = selectedConfig.timerPrompt ?? 0;
    const reactDontReactTo =
      selectedConfig.neurocognitiveType === 0
        ? []
        : selectedConfig.dontReactTo;
    const verticalReact =
      selectedConfig.jumpType !== EExerciseJumpType.DROP_JUMP &&
      Boolean(selectedConfig.verticalReact);

    formGroup.controls.config.controls.sequenceForm.controls.sequenceSensors.clear();
    selectedConfig?.sensorsSequence?.forEach((val) =>
        formGroup.controls.config.controls.sequenceForm.controls.sequenceSensors.push(
            new FormControl(val),
        ),
    );

    formGroup.controls.config.controls.agilityForm.controls.agilitySensors.clear();
    selectedConfig?.arrows?.forEach((arrow: number) =>
        formGroup.controls.config.controls.agilityForm.controls.agilitySensors.push(
            new FormControl(arrow),
        ),
    );

    const companionForm = formGroup.controls.config.controls.companionForm;
    if (companionForm) {
      companionForm.controls.sequenceSensors.clear();
      selectedConfig.sensorsSequence?.forEach((sensor: number) =>
        companionForm.controls.sequenceSensors.push(new FormControl(sensor)),
      );
      companionForm.controls.simultaneousSensors.clear();
      selectedConfig.activeSensors?.forEach((sensor: number) =>
        companionForm.controls.simultaneousSensors.push(new FormControl(sensor)),
      );
      companionForm.controls.arrows.clear();
      selectedConfig.arrows?.forEach((arrow: number) =>
        companionForm.controls.arrows.push(new FormControl(arrow)),
      );
    }


    const value = {
      config: {
        difficulty: selectedConfig?.difficulty,
        countdownForm: {
          startType: selectedConfig?.countdownStartType ??
            (selectedConfig?.countdownStart == null
              ? EExerciseStartType.RANDOM
              : EExerciseStartType.FULL_COUNTDOWN),
          startValue: selectedConfig?.countdownStart,
          endType: selectedConfig.countdownEndType,
          endValue: selectedConfig.countdownEnd,
        },
        countForm: {
          countType: selectedConfig.sumType,
        },
        reactForm: {
          delay: selectedConfig.timerDelay,
          timerPrompt,
          neurocognitiveType: selectedConfig.neurocognitiveType,
          stroopEffectType: selectedConfig.stroopEffectType,
          liftTime: selectedConfig.liftTime,
          footFireActivity: selectedConfig.footFireActivity,
          isolatedReact: selectedConfig.isolatedReact,
          reactTo: new Array(8).fill(false).map((value, index) => {
            return selectedConfig?.reactTo?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((value, index) => {
            return reactDontReactTo?.includes(index);
          }),
          activeSensors: new Array(5).fill(false).map((value, index) => {
            return selectedConfig?.activeSensors?.includes(index + 1);
          }),
        },
        sequenceForm: {
          delay: selectedConfig.timerDelay,
          sequenceSensors: selectedConfig.sensorsSequence,
          timerPrompt,
        },
        agilityForm: {
          delay: selectedConfig.timerDelay,
          agilityCustomType: selectedConfig.arrowsType,
          agilitySensors: selectedConfig.arrows,
          timerPrompt
        },
        verticalForm: {
          numberOfLegs: selectedConfig.numberOfLegs,
          verticalJumpType: selectedConfig.jumpType,
          numberOfJumps: selectedConfig.jumpCount,
          isVerticalReact: verticalReact,
          reactTo: new Array(8).fill(false).map((value, index) => {
            return verticalReact && selectedConfig?.reactTo?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((value, index) => {
            return verticalReact && selectedConfig?.dontReactTo?.includes(index);
          }),
        },
        companionForm: {
          companionMode: (selectedConfig as any)?.modeType ?? 0,
          numberOfCompanions: (selectedConfig as any)?.companionsCount ?? 1,
          numberOfSensors: (selectedConfig as any)?.sensorsCount ?? 1,
          agilityType: (selectedConfig as any)?.agilityType ?? 1,
          numberType: (selectedConfig as any)?.numberType ?? 0,
          arrowsType: (selectedConfig as any)?.arrowsType ?? 0,
          arrows: (selectedConfig as any)?.arrows ?? [],
          countIncrementBy: (selectedConfig as any)?.countIncrementBy ?? 1,
          countDirection: (((selectedConfig as any)?.countDirectionType ?? 0) === 0 ? 'Ascending' : 'Descending') as 'Ascending' | 'Descending',
          complexityLevel: ((): any => {
            const v = (selectedConfig as any)?.complexityLevel ?? 0;
            if (v === 2) return 'Advanced'; if (v === 1) return 'Intermediate'; return 'Beginner';
          })(),
          reactTo: new Array(8).fill(false).map((_, index) => {
            return (selectedConfig?.reactTo as number[])?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((_, index) => {
            return (selectedConfig?.dontReactTo as number[])?.includes(index);
          }),
          delay: (selectedConfig as any)?.timerDelay ?? 0,
          timerPrompt,
          communicationType: (selectedConfig as any)?.reactCommunicationType ?? 0,
          neurocognitiveType: (selectedConfig as any)?.neurocognitiveType ?? 0,
          stroopEffectType: (selectedConfig as any)?.stroopEffectType ?? 0,
          isolatedReact: (selectedConfig as any)?.isolatedReact ?? false,
          activeSensors: new Array(5).fill(false).map((_, index) => {
            return (selectedConfig as any)?.activeSensors?.includes(index + 1);
          }),
          simultaneousType: (selectedConfig as any)?.simultaneousType ?? 0,
          simultaneousSensors: (selectedConfig as any)?.activeSensors ?? [],
          sequenceSensors: (selectedConfig as any)?.sensorsSequence ?? [],
          startValue: (selectedConfig as any)?.startValue ?? 1,
          endValue: (selectedConfig as any)?.endValue ?? 10,
          limitLow: (selectedConfig as any)?.startValue ?? 1,
          limitHigh: (selectedConfig as any)?.endValue ?? 10,
        }
      },
    };
    if (
      value.config.countdownForm.startType === EExerciseStartType.RANDOM
    ) {
      delete value.config.countdownForm.startValue;
    }

    formGroup.patchValue(value);
  }

  static patchFormValue(
    formGroup: FormGroup<IRegularExerciseForm>,
    exercise,
  ): boolean {
    const selectedConfig = exercise.configs.find(
      (config) => config.id === exercise.defaultConfigId,
    );
    if(!selectedConfig) return false;
    const reactDontReactTo =
      selectedConfig.neurocognitiveType === 0
        ? []
        : selectedConfig.dontReactTo;
    const verticalReact =
      selectedConfig.jumpType !== EExerciseJumpType.DROP_JUMP &&
      Boolean(selectedConfig.verticalReact);
    formGroup.controls.config.controls.sequenceForm.controls.sequenceSensors.clear();
    selectedConfig?.sensorsSequence?.forEach((val) =>
      formGroup.controls.config.controls.sequenceForm.controls.sequenceSensors.push(
        new FormControl(val),
      ),
    );

    // Prefill companion simultaneous sensors sequence (same approach as Sequence)
    formGroup.controls.config.controls.companionForm?.controls.sequenceSensors?.clear();
    selectedConfig?.sensorsSequence?.forEach((val: number) =>
      formGroup.controls.config.controls.companionForm?.controls.sequenceSensors?.push(
        new FormControl(val),
      ),
    );
    formGroup.controls.config.controls.companionForm?.controls.simultaneousSensors?.clear();
    selectedConfig?.activeSensors?.forEach((sensor: number) =>
      formGroup.controls.config.controls.companionForm?.controls.simultaneousSensors?.push(
        new FormControl(sensor),
      ),
    );
    formGroup.controls.config.controls.agilityForm.controls.agilitySensors.clear();
    formGroup.controls.config.controls.companionForm?.controls.arrows?.clear();
    selectedConfig?.arrows?.forEach((arrow: number) =>
      formGroup.controls.config.controls.companionForm?.controls.arrows?.push(
        new FormControl(arrow),
      ),
    );
    const timerPrompt = selectedConfig.timerPrompt ?? 0;
    const value = {
      category: {
        id: exercise.categoryId,
        name: null,
      },
      name: exercise.name,
      description: RegularExerciseHelper.descriptionToPlainText(exercise.description ?? ""),
      originalDescriptionHtml: exercise.description ?? "",
      type: exercise.type,
      video: { fileUrl: exercise.videoUrl, file: null},
      imgThumb: exercise.thumbnailUrl ?? exercise.imageUrl ?? "",
      bodyPart: exercise.bodyPart,
      config: {
        difficulty: selectedConfig?.difficulty,
        countdownForm: {
          startType: selectedConfig?.countdownStartType ??
            (selectedConfig?.countdownStart == null
              ? EExerciseStartType.RANDOM
              : EExerciseStartType.FULL_COUNTDOWN),
          startValue: selectedConfig?.countdownStart,
          endType: selectedConfig.countdownEndType,
          endValue: selectedConfig.countdownEnd,
        },
        countForm: {
          countType: selectedConfig.sumType,
        },
        reactForm: {
          delay: selectedConfig.timerDelay,
          timerPrompt,
          stroopEffectType: selectedConfig.stroopEffectType,
          liftTime:  selectedConfig.liftTime,
          footFireActivity:  selectedConfig.footFireActivity,
          neurocognitiveType: selectedConfig.neurocognitiveType,
          isolatedReact: selectedConfig.isolatedReact,
          reactTo: new Array(8).fill(false).map((value, index) => {
            return selectedConfig?.reactTo?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((value, index) => {
            return reactDontReactTo?.includes(index);
          }),
          activeSensors: new Array(5).fill(false).map((value, index) => {
            return selectedConfig?.activeSensors?.includes(index + 1);
          }),
        },
        sequenceForm: {
          delay: selectedConfig.timerDelay,
          sequenceSensors: selectedConfig.sensorsSequence,
          timerPrompt,
        },
        agilityForm: {
          delay: selectedConfig.timerDelay,
          agilityCustomType: selectedConfig.arrowsType,
          agilitySensors: selectedConfig.arrows,
          timerPrompt
        },
        verticalForm: {
          numberOfLegs: selectedConfig.numberOfLegs,
          verticalJumpType: selectedConfig.jumpType,
          numberOfJumps: selectedConfig.jumpCount,
          isVerticalReact: verticalReact,
          reactTo: new Array(8).fill(false).map((value, index) => {
            return verticalReact && selectedConfig?.reactTo?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((value, index) => {
            return verticalReact && selectedConfig?.dontReactTo?.includes(index);
          }),
        },
        companionForm: {
          companionMode: (selectedConfig as any)?.modeType ?? 0,
          numberOfCompanions: (selectedConfig as any)?.companionsCount ?? 1,
          numberOfSensors: (selectedConfig as any)?.sensorsCount ?? 1,
          agilityType: (selectedConfig as any)?.agilityType ?? 1,
          numberType: (selectedConfig as any)?.numberType ?? 0,
          arrowsType: (selectedConfig as any)?.arrowsType ?? 0,
          arrows: (selectedConfig as any)?.arrows ?? [],
          countIncrementBy: (selectedConfig as any)?.countIncrementBy ?? 1,
          countDirection: (((selectedConfig as any)?.countDirectionType ?? 0) === 0 ? 'Ascending' : 'Descending') as 'Ascending' | 'Descending',
          complexityLevel: ((): any => {
            const v = (selectedConfig as any)?.complexityLevel ?? 0;
            if (v === 2) return 'Advanced'; if (v === 1) return 'Intermediate'; return 'Beginner';
          })(),
          reactTo: new Array(8).fill(false).map((_, index) => {
            return (selectedConfig?.reactTo as number[])?.includes(index);
          }),
          dontReactTo: new Array(8).fill(false).map((_, index) => {
            return (selectedConfig?.dontReactTo as number[])?.includes(index);
          }),
          delay: (selectedConfig as any)?.timerDelay ?? 0,
          timerPrompt,
          communicationType: (selectedConfig as any)?.reactCommunicationType ?? 0,
          neurocognitiveType: (selectedConfig as any)?.neurocognitiveType ?? 0,
          stroopEffectType: (selectedConfig as any)?.stroopEffectType ?? 0,
          isolatedReact: (selectedConfig as any)?.isolatedReact ?? false,
          activeSensors: new Array(5).fill(false).map((value, index) => {
            return (selectedConfig as any)?.activeSensors?.includes(index + 1);
          }),
          simultaneousType: (selectedConfig as any)?.simultaneousType ?? 0,
          simultaneousSensors: (selectedConfig as any)?.activeSensors ?? [],
          sequenceSensors: (selectedConfig as any)?.sensorsSequence ?? [],
          startValue: (selectedConfig as any)?.startValue ?? 1,
          endValue: (selectedConfig as any)?.endValue ?? 10,
          limitLow: (selectedConfig as any)?.startValue ?? 1,
          limitHigh: (selectedConfig as any)?.endValue ?? 10,
        }
      },
    };
    if (
      value.config.countdownForm.startType === EExerciseStartType.RANDOM
    ) {
      delete value.config.countdownForm.startValue;
    }

    selectedConfig?.arrows?.forEach((arrow: number) =>
      formGroup.controls.config.controls.agilityForm.controls.agilitySensors.push(
        new FormControl(arrow),
      ),
    );

    formGroup.patchValue(value);
    return true;
  }
}
